import React from 'react';
import { Box, Chip, IconButton, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import { IconEdit, IconTrash } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { BorderedGrid } from '@shared/components/borderedGrid';
import { truncateText } from '@shared/utils/truncateText';
import { McpToolCardProps, McpServerConfig } from './mcp.types';

const McpToolCard: React.FC<McpToolCardProps> = ({ tool, agents, onEdit, onDelete, disabled }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const config = tool.mcpServerConfig as McpServerConfig | undefined;
  const linkedAgent = tool.agentId ? agents.find((a) => a.id === tool.agentId) : null;

  const headersCount = config?.headers ? Object.keys(config.headers).length : 0;

  return (
    <BorderedGrid
      sx={{
        marginBottom: '12px',
        padding: '16px 20px',
        transition: 'border-color 0.2s ease',
        '&:hover': {
          borderColor: theme.palette.primary.main,
        },
      }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
            <Typography
              variant="subtitle1"
              fontWeight={600}
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
              {tool.mcpName || t('mcpTools.unnamed')}
            </Typography>
            {linkedAgent ? (
              <Chip
                label={linkedAgent.name}
                size="small"
                sx={{
                  backgroundColor: theme.palette.primary.light,
                  color: theme.palette.primary.main,
                  fontWeight: 500,
                  fontSize: '11px',
                  height: '22px',
                }}
              />
            ) : (
              <Chip
                label={t('mcpTools.global')}
                size="small"
                sx={{
                  backgroundColor: theme.palette.grey[200],
                  color: theme.palette.grey[700],
                  fontWeight: 500,
                  fontSize: '11px',
                  height: '22px',
                }}
              />
            )}
          </Stack>

          <Stack direction="row" alignItems="center" spacing={2}>
            <Tooltip title={config?.url || ''} placement="bottom-start">
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '350px',
                }}>
                {truncateText(config?.url || '', 45)}
              </Typography>
            </Tooltip>
            {headersCount > 0 && (
              <Tooltip title={Object.keys(config?.headers || {}).join(', ')} placement="bottom">
                <Typography variant="body2" color="text.secondary">
                  • {t('mcpTools.headersCount', { count: headersCount })}
                </Typography>
              </Tooltip>
            )}
          </Stack>
        </Box>

        <Stack direction="row" spacing={0.5}>
          <Tooltip title={t('common.edit')}>
            <span>
              <IconButton
                size="small"
                onClick={() => onEdit(tool)}
                disabled={disabled}
                sx={{
                  color: theme.palette.primary.main,
                  '&:hover': {
                    backgroundColor: theme.palette.primary.light,
                  },
                }}>
                <IconEdit size={18} />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={t('common.delete')}>
            <span>
              <IconButton
                size="small"
                onClick={() => onDelete(tool.id)}
                disabled={disabled}
                sx={{
                  color: theme.palette.error.main,
                  '&:hover': {
                    backgroundColor: theme.palette.error.light,
                  },
                }}>
                <IconTrash size={18} />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Stack>
    </BorderedGrid>
  );
};

export default McpToolCard;
